import React, {useEffect, useRef, useState} from "react";
import {
  Box,
  Flex,
  CircularProgress,
  CircularProgressLabel,
  Text,
  VStack,
  IconButton
} from "@chakra-ui/react";
import {ChevronLeftIcon, ChevronRightIcon} from "@chakra-ui/icons";
import {MdCloudUpload} from "react-icons/md";
import {IUploadItem} from "../models/interfaces/uploadItem.interface";

interface UploadToasterProps {
  uploads: IUploadItem[];
}

const UploadToaster: React.FC<UploadToasterProps> = ({uploads}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!isOpen) {
      timer = setTimeout(() => {
        setShowToggle(true);
      }, 300); // matches the CSS transition
    } else {
      setShowToggle(false);
      setIsHovering(false)
    }
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  if (uploads.length === 0) return null;


  return (
    <Box
      position="fixed"
      bottom="20px"
      left={isOpen ? "0" : "-280px"}
      transition="left 0.3s ease-in-out"
      zIndex={1500}
    >
      {/* Upload Drawer */}
      <Box
        ref={drawerRef}
        className="w-[280px] p-2 bg-gray-700 text-white rounded-r-xl rounded-br-xl"
        boxShadow="xl"
      >
        <Flex className="items-center gap-2 z-1 sticky top-0 mb-1">
          <IconButton
            size="sm"
            aria-label="Close upload drawer"
            icon={<ChevronLeftIcon color="white" boxSize={6}/>}
            onClick={() => setIsOpen(false)}
            bg="transparent"
            _hover={{bg: "gray.600"}}
          />
          <Text>Uploads</Text>
        </Flex>

        <VStack align="start" spacing={2} maxHeight="160px" overflowY="auto">
          {uploads.map((upload: IUploadItem) => (
            <Flex key={upload.fileName} align="center" gap={3}>
              <CircularProgress
                value={upload.progress}
                size="40px"
                color={upload.status === "success" ? "green.400" : "blue.400"}
              >
                <CircularProgressLabel>{upload.progress}%</CircularProgressLabel>
              </CircularProgress>
              <Text noOfLines={1} title={upload.fileName} flex={1}>
                {upload.fileName}
              </Text>
            </Flex>
          ))}
        </VStack>
      </Box>

      {/* Toggle Tab */}
      {showToggle && (
        <Flex
          position="absolute"
          right="-40px"
          bottom="20px"
          width="40px"
          height="60px"
          align="center"
          justify="center"
          bg="brandPurple.500"
          borderRightRadius="md"
          cursor="pointer"
          onClick={() => setIsOpen(true)}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {isHovering ? (
            <ChevronRightIcon color="white" boxSize={6}/>
          ) : (
            <MdCloudUpload color="white" size={24}/>
          )}
        </Flex>
      )}
    </Box>
  );
};

export default UploadToaster;